<script>
    import {createEvent} from "shared/eventBus";
    import spinner from "shared/Components/spinner.vue";
    
    export default {
        props: {
            items: Array,
            value: String | Number,
            name: String,
            disabled: Boolean,
            height: {
                type: String,
                default: "40px"
            },
            width: {
                type: String,
                default: "100%"
            },
            saved: Boolean,
            loading: Boolean
        },
        data() {
            return {
                selectedValue: null,
                isNumber: false,
                changed: false,
                opened: false
            }
        },
        components: {
            spinner
        },
        methods: {
            valueChanged(value) {
                this.selectValue(value);
                if(this.isNumber) {
                    value = Number(value);
                }
                this.$emit('selected', value);
                this.$emit('input', value);
                this.$emit('change', value);
                this.changed = true;
                this.$el.dispatchEvent(createEvent("change"));
            },
            selectValue(val) {
                this.selectedValue = val;
                this.opened = false;

                const selectedOption = Array.from(this.$refs.select.options).find(opt => opt.value === val);
                if(selectedOption) {
                    this.$refs.container.setAttribute("data-content", selectedOption.text);
                } else {
                    this.$refs.container.setAttribute("data-content", "");
                }
            },
            focus() {
                this.$refs.select.focus();
            }
        },
        mounted() {
            this.isNumber = typeof(this.value) === "number";
            this.selectValue(this.value);

            let isScrolling;

            window.addEventListener('scroll', () => {
                clearTimeout(isScrolling);
                isScrolling = setTimeout(() => this.opened = false, 66);
            }, false);
        },
        watch: {
            value(val) {
                this.isNumber = typeof(val) === "number";
                this.selectValue(val);
            },
            saved(val) {
                if(val) {
                    this.changed = false;
                }
            }
        }
    }
</script>

<template>
    <div ref="container" class="select-container" :class="{disabled: disabled}" :disabled="disabled" :style="{ height: height, width: width }">
        <select ref="select" :name="name" v-model="selectedValue" @click.prevent="opened = !opened" @blur="opened = false"
            @change="valueChanged($event.target.value)" :disabled="disabled">
            <slot></slot>
            <option v-for="item in items" :value="item.value" :key="item.value" :aria-label="item.ariaLabel">{{item.text}}</option>
        </select>
        <span v-if="!loading" ref="flipImg" :class="['select-arrow', { flip: opened }]" :style="{ height: height, width: height }">
        </span>
        <spinner v-else :visible="loading" half></spinner>
    </div>
</template>

<style lang="less" scoped>
    @import "~less-variables";
    @import "~less-mixins";

    .select-container {
        position: relative;
        width: 100%;
        height: 40px;
        display: inline-block;
        border: 1px solid @frontier-gray-xxlighter;
        
        select::-ms-expand {
            display: none;
        } 
        
        select {
            cursor: pointer;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
            -moz-border-radius: 2px;
            -webkit-border-radius: 2px;
            border-radius: 2px;
            text-indent: 1px;
            text-overflow: '';
            width: 100%;
            height: 100%;
            padding-left: 10px;
            padding-top: 9px;
            padding-bottom: 7px;
            padding-right: 30px;
            font-family: @frontierFontFamily;
            font-size: 16px;
            background: @white;

            @media @Firefox {
                padding-top: 6px;
                padding-bottom: 5px;
            }

            &[disabled] {
                border: 1px solid #9a9a9b;
                box-shadow: inset 0 100px 0px 0px @inputDisabledBackground !important;
                background-color: @inputDisabledBackground;
            }
        }

        .select-arrow {
            position: absolute;
            right: 0;
            top: 0;
            text-align: center;
            z-index: 1;
            line-height: 38px;
            pointer-events: none;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: transparent url(https://flyfrontier-com-frontdoor-en-chh3dufqdpdff8h9.a02.azurefd.net/images/down-arrow-gray.png) no-repeat center center;
            transition: transform 300ms ease-in-out;
                .rotate(360deg);
            &.flip {
                .rotate(180deg);
            }
        }

        &[disabled] {
            box-shadow: inset 0 100px 0px 0px @inputDisabledBackground !important;
            background-color: @inputDisabledBackground;
            cursor: default;

            .select-arrow {
                display: none;
            }
        }

        .lds-ring {
            position:absolute !important;
            right: 0;
            top: 0;
            width: 100% !important;
            height: 100% !important;
            background-color: rgba(255,255,255,0.4);
            cursor: default;

            @{deep} div {
                right: 12px;
                left: auto;
            }
        }

        &::before {
            content: attr(data-content);
            background: @white;
            color: @frontier-gray-xxdark;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            padding-left: 12px;
            padding-top: 11px;
            padding-bottom: 7px;
            padding-right: 33px;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            text-transform: capitalize;
            pointer-events: none;
        }

        &.disabled {
            &::before {
                background: @inputDisabledBackground;
            }
        }
    }

body.user-is-tabbing {
    .select-container:focus-within {
        border: 4px solid #aaa;
        box-sizing: content-box;
    }
}
</style>